import { useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, QrCode } from "lucide-react";
import { WebsiteTabs } from "@/components/website/WebsiteTabs";
import { DayBars } from "@/components/website/qr/DayBars";
import { NewQrCodeDialog } from "@/components/website/qr/NewQrCodeDialog";
import { QrCodeDetail } from "@/components/website/qr/QrCodeDetail";
import { useWebsiteStats } from "@/hooks/useWebsiteSubmissions";
import { useQrCodes, type QrCodeRow } from "@/hooks/useQrCodes";
import { shortDestination } from "@shared/qrCodes";

function relative(iso: string | null): string {
  if (!iso) return "Never";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Row({ row, onOpen }: { row: QrCodeRow; onOpen: () => void }) {
  const who = row.contactName ?? row.companyName;
  return (
    <button onClick={onOpen} className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-b border-border px-3 py-2.5 text-left hover:bg-muted/50 md:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_90px_150px_90px]">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{row.name}</span>
          {row.pausedAt && <span className="rounded bg-muted px-1.5 text-[11px] text-muted-foreground">Paused</span>}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {who ? `For ${who}` : "Placement code"} · {shortDestination(row.destinationUrl)}
        </div>
      </div>
      <div className="hidden truncate text-xs text-muted-foreground md:block">/q/{row.code}</div>
      <div className="hidden text-right text-sm tabular-nums md:block">{row.scans30d}</div>
      <div className="hidden text-right text-sm tabular-nums md:block">{row.unique30d}</div>
      <div className="hidden md:block"><DayBars values={row.daily30} height={20} /></div>
      <div className="text-right text-xs text-muted-foreground">{relative(row.lastScanAt)}</div>
    </button>
  );
}

export default function WebsiteQr() {
  const [archived, setArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const stats = useWebsiteStats();
  const list = useQrCodes(archived);
  const rows = list.data ?? [];
  const scans30 = rows.reduce((n, r) => n + r.scans30d, 0);

  const counts = {
    ...Object.fromEntries((stats.data?.byType ?? []).map((b) => [b.source_type, b.total])),
    ...(archived ? {} : { qr: rows.length }),
  };

  return (
    <AppLayout title="Website">
      <div className="space-y-5">
        <header className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">QR codes</div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-semibold tabular-nums">{list.isLoading ? "—" : scans30}</span>
              <span className="text-sm text-muted-foreground">scans in the last 30 days</span>
            </div>
          </div>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> New QR code
          </Button>
        </header>

        <WebsiteTabs active="qr" counts={counts} />

        <div className="flex items-center justify-end">
          <button onClick={() => setArchived((a) => !a)} className="text-xs text-muted-foreground hover:text-foreground">
            {archived ? "Show active codes" : "Show archived"}
          </button>
        </div>

        <div className="overflow-hidden rounded-lg border border-border">
          <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_90px_90px_150px_90px] gap-4 border-b border-border bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
            <span>Code</span><span>Link</span><span className="text-right">Scans 30d</span>
            <span className="text-right">Devices 30d</span><span>Daily</span><span className="text-right">Last scan</span>
          </div>
          {list.isLoading ? (
            <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
              <QrCode className="h-6 w-6" />
              {archived ? "No archived codes." : "No QR codes yet. Make one for a booth, a brochure or a letter."}
            </div>
          ) : (
            rows.map((r) => <Row key={r.id} row={r} onOpen={() => setOpenId(r.id)} />)
          )}
        </div>
        <NewQrCodeDialog open={creating} onOpenChange={setCreating} />
        <QrCodeDetail row={rows.find((r) => r.id === openId) ?? null} onClose={() => setOpenId(null)} />
      </div>
    </AppLayout>
  );
}
